import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clearAdminSession, getAdmin, getAdminToken } from '../lib/adminAuth';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';

function NavItem({ icon, label, active, badge }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px',
      borderRadius: 7,
      background: active ? 'rgba(243,116,40,0.15)' : 'transparent',
      borderLeft: active ? `3px solid ${ORANGE}` : '3px solid transparent',
      color: active ? ORANGE : 'rgba(255,255,255,0.55)',
      fontSize: 12, fontWeight: active ? 600 : 400,
      cursor: 'pointer', marginBottom: 1, transition: 'all 0.12s',
    }}>
      <span style={{ fontSize: 13, width: 18, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge != null && (
        <span style={{ background: ORANGE, color: '#fff', borderRadius: 10, fontSize: 9, fontWeight: 700, padding: '1px 6px' }}>
          {badge}
        </span>
      )}
    </div>
  );
}

const SECTION_MEMBERS = {
  customers:  ['tenants','revenue','discounts','api-keys','partners'],
  operations: ['jobs','enrichment-audit','ai-engine','email-templates','visitors','activity','security','surveys'],
  agents:     ['finn','dash','dash-analytics','monitor','ops-ai','chat'],
  b2b:        ['b2b-accounts','b2b-contacts','b2b-lists','b2b-templates','b2b-streams','b2b-jobs','b2b-classify','b2b-builder','b2b-apollo-pull'],
  system:     ['users','bible'],
};

function NavSection({ id, icon, label, children, active, stored, onToggle, badge }) {
  const hasActive = SECTION_MEMBERS[id]?.includes(active);
  const [open, setOpen] = useState(() => {
    if (hasActive) return true;
    if (stored !== undefined) return stored;
    return id === 'customers';
  });

  useEffect(() => {
    if (hasActive && !open) setOpen(true);
  }, [active]);

  function toggle() {
    const next = !open;
    setOpen(next);
    onToggle(id, next);
  }

  return (
    <div style={{ marginBottom: 2 }}>
      <div onClick={toggle} style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px',
        cursor: 'pointer', borderRadius: 8,
        color: hasActive ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.45)',
        fontSize: 12, fontWeight: 600,
        userSelect: 'none',
        background: hasActive ? 'rgba(255,255,255,0.04)' : 'transparent',
        transition: 'all 0.12s',
      }}>
        <span style={{ fontSize: 13, width: 20, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        {badge != null && (
          <span style={{ background: ORANGE, color: '#fff', borderRadius: 10, fontSize: 9, fontWeight: 700, padding: '1px 6px', marginRight: 2 }}>
            {badge}
          </span>
        )}
        <span style={{ fontSize: 11, opacity: 0.5, transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
      </div>
      <div style={{
        overflow: 'hidden',
        maxHeight: open ? 600 : 0,
        transition: 'max-height 0.22s ease',
        paddingLeft: 6,
      }}>
        {children}
      </div>
    </div>
  );
}

export default function AdminSidebar({ active }) {
  const navigate = useNavigate();
  const admin = getAdmin();
  const [finnPending, setFinnPending] = useState(0);

  useEffect(() => {
    const token = getAdminToken();
    if (!token) return;
    const API_BASE = import.meta.env.VITE_API_URL || 'https://masterfileai-api.azurewebsites.net';
    fetch(`${API_BASE}/api/console/finn/summary?token=${token}`, {
      headers: { 'Content-Type': 'text/plain' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(s => s?.pending && setFinnPending(s.pending))
      .catch(() => {});
  }, []);

  const [sections, setSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_sidebar_sections') || '{}'); }
    catch { return {}; }
  });

  function handleToggle(id, isOpen) {
    const next = { ...sections, [id]: isOpen };
    setSections(next);
    localStorage.setItem('admin_sidebar_sections', JSON.stringify(next));
  }

  function handleLogout() { clearAdminSession(); navigate('/admin/login'); }

  return (
    <aside style={s.sidebar}>
      {/* Logo */}
      <div style={s.logoRow}>
        <img src="https://www.masterfileai.com/hubfs/logo3.png" alt="MasterFileAI"
          style={{ height: 26, maxWidth: 140, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
          onError={e => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }} />
        <div style={{ display: 'none', alignItems: 'center', gap: 8 }}>
          <div style={s.logoMark}>M</div>
          <span style={s.logoText}>MasterFileAI</span>
        </div>
      </div>

      <div style={s.adminTag}>ADMIN CONSOLE</div>

      <nav style={{ flex: 1, marginTop: 4, overflowY: 'auto' }}>

        {/* Dashboard — always visible */}
        <Link to="/admin/dashboard" style={{ textDecoration: 'none' }}>
          <NavItem icon="▦" label="Dashboard" active={active === 'dashboard'} />
        </Link>

        <div style={s.divider} />

        <NavSection id="customers" icon="🏢" label="Customers" active={active} stored={sections.customers} onToggle={handleToggle}>
          <Link to="/admin/tenants"   style={{ textDecoration: 'none' }}><NavItem icon="🏢" label="Tenants"   active={active === 'tenants'} /></Link>
          <Link to="/admin/revenue"   style={{ textDecoration: 'none' }}><NavItem icon="💰" label="Revenue"   active={active === 'revenue'} /></Link>
          <Link to="/admin/discounts" style={{ textDecoration: 'none' }}><NavItem icon="🏷" label="Discounts" active={active === 'discounts'} /></Link>
          <Link to="/admin/api-keys"  style={{ textDecoration: 'none' }}><NavItem icon="🔑" label="API Keys"  active={active === 'api-keys'} /></Link>
          <Link to="/admin/partners"  style={{ textDecoration: 'none' }}><NavItem icon="🤝" label="Partners"  active={active === 'partners'} /></Link>
        </NavSection>

        <NavSection id="operations" icon="⚙" label="Operations" active={active} stored={sections.operations} onToggle={handleToggle}>
          <Link to="/admin/jobs"             style={{ textDecoration: 'none' }}><NavItem icon="⚙"  label="Job Queue"        active={active === 'jobs'} /></Link>
          <Link to="/admin/enrichment-audit" style={{ textDecoration: 'none' }}><NavItem icon="🔍" label="Enrichment Audit" active={active === 'enrichment-audit'} /></Link>
          <Link to="/admin/ai-engine"        style={{ textDecoration: 'none' }}><NavItem icon="🧠" label="AI Engine"        active={active === 'ai-engine'} /></Link>
          <Link to="/admin/email-templates"  style={{ textDecoration: 'none' }}><NavItem icon="✉"  label="Email Templates" active={active === 'email-templates'} /></Link>
          <Link to="/admin/visitors"         style={{ textDecoration: 'none' }}><NavItem icon="👁" label="Web Visitors"    active={active === 'visitors'} /></Link>
          <Link to="/admin/activity"         style={{ textDecoration: 'none' }}><NavItem icon="📋" label="Activity"        active={active === 'activity'} /></Link>
          <Link to="/admin/security"         style={{ textDecoration: 'none' }}><NavItem icon="🔒" label="Security"        active={active === 'security'} /></Link>
          <Link to="/admin/surveys"          style={{ textDecoration: 'none' }}><NavItem icon="📝" label="Surveys"         active={active === 'surveys'} /></Link>
        </NavSection>

        <NavSection id="agents" icon="🤖" label="AI Agents" active={active} stored={sections.agents} onToggle={handleToggle} badge={finnPending > 0 ? finnPending : null}>
          <Link to="/admin/finn"    style={{ textDecoration: 'none' }}><NavItem icon="🐾" label="Finn"  active={active === 'finn'}    badge={finnPending > 0 ? finnPending : null} /></Link>
          <Link to="/admin/dash"    style={{ textDecoration: 'none' }}><NavItem icon="⚡" label="Dash"  active={active === 'dash'} /></Link>
          <Link to="/admin/dash/analytics" style={{ textDecoration: 'none' }}><NavItem icon="📊" label="Dash Analytics" active={active === 'dash-analytics'} /></Link>
          <Link to="/admin/monitor" style={{ textDecoration: 'none' }}><NavItem icon="🔭" label="Scout" active={active === 'monitor'} /></Link>
          <Link to="/admin/ops-ai"  style={{ textDecoration: 'none' }}><NavItem icon="🦅" label="Hawk"  active={active === 'ops-ai'} /></Link>
          <Link to="/admin/chat"    style={{ textDecoration: 'none' }}><NavItem icon="🌸" label="Tilly" active={active === 'chat'} /></Link>
        </NavSection>

        <NavSection id="b2b" icon="🎯" label="B2B Prospecting" active={active} stored={sections.b2b} onToggle={handleToggle}>
          <Link to="/admin/b2b/accounts"   style={{ textDecoration: 'none' }}><NavItem icon="🏢" label="Accounts"        active={active === 'b2b-accounts'} /></Link>
          <Link to="/admin/b2b/contacts"   style={{ textDecoration: 'none' }}><NavItem icon="👤" label="Contacts"        active={active === 'b2b-contacts'} /></Link>
          <Link to="/admin/b2b/lists"      style={{ textDecoration: 'none' }}><NavItem icon="📋" label="Lists"           active={active === 'b2b-lists'} /></Link>
          <Link to="/admin/b2b/templates"  style={{ textDecoration: 'none' }}><NavItem icon="✍"  label="Email Templates" active={active === 'b2b-templates'} /></Link>
          <Link to="/admin/b2b/streams"    style={{ textDecoration: 'none' }}><NavItem icon="⚡" label="Email Streams"   active={active === 'b2b-streams'} /></Link>
          <Link to="/admin/b2b/jobs"       style={{ textDecoration: 'none' }}><NavItem icon="🚀" label="Marketing Jobs"  active={active === 'b2b-jobs'} /></Link>
          <Link to="/admin/b2b/classify"   style={{ textDecoration: 'none' }}><NavItem icon="✦"  label="Classify Titles" active={active === 'b2b-classify'} /></Link>
          <Link to="/admin/b2b/builder"    style={{ textDecoration: 'none' }}><NavItem icon="✉"  label="Template Builder" active={active === 'b2b-builder'} /></Link>
          <Link to="/admin/b2b/apollo-pull" style={{ textDecoration: 'none' }}><NavItem icon="🚀" label="Apollo Pull"    active={active === 'b2b-apollo-pull'} /></Link>
        </NavSection>

        <NavSection id="system" icon="🔧" label="System" active={active} stored={sections.system} onToggle={handleToggle}>
          {admin?.role === 'superadmin' && (
            <Link to="/admin/users" style={{ textDecoration: 'none' }}><NavItem icon="🔐" label="Admin Users" active={active === 'users'} /></Link>
          )}
          <a href="/project.html" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <NavItem icon="📖" label="The Bible" active={active === 'bible'} />
          </a>
        </NavSection>

      </nav>

      {/* Footer */}
      <div style={s.footer}>
        <div style={s.userChip}>
          <div style={s.avatar}>{admin?.full_name?.[0]?.toUpperCase() || 'A'}</div>
          <div>
            <div style={s.userName}>{admin?.full_name || 'Admin'}</div>
            <div style={s.userRole}>{admin?.role || 'admin'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Link to="/admin/change-password" style={{ textDecoration: 'none', flex: 1 }}>
            <button style={s.logoutBtn}>Change password</button>
          </Link>
          <button onClick={handleLogout} style={s.logoutBtn}>Sign out</button>
        </div>
      </div>
    </aside>
  );
}

const s = {
  sidebar:   { width: 220, background: NAVY, display: 'flex', flexDirection: 'column', padding: '20px 10px', flexShrink: 0 },
  logoRow:   { marginBottom: 4, paddingLeft: 4, minHeight: 28, display: 'flex', alignItems: 'center' },
  logoMark:  { width: 28, height: 28, borderRadius: 7, background: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 },
  logoText:  { color: '#fff', fontWeight: 700, fontSize: 14 },
  adminTag:  { color: ORANGE, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', paddingLeft: 14, marginBottom: 12, marginTop: 8 },
  divider:   { borderTop: '1px solid rgba(255,255,255,0.07)', margin: '6px 0' },
  footer:    { borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12, marginTop: 8 },
  userChip:  { display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 },
  avatar:    { width: 28, height: 28, borderRadius: '50%', background: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 11, flexShrink: 0 },
  userName:  { color: '#fff', fontSize: 11, fontWeight: 600 },
  userRole:  { color: 'rgba(255,255,255,0.4)', fontSize: 10, textTransform: 'capitalize' },
  logoutBtn: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: 'rgba(255,255,255,0.5)', fontSize: 11, padding: '5px 10px', width: '100%', textAlign: 'left', cursor: 'pointer' },
};
